import React, { useMemo, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { Box, Chip, MenuItem, TextField, InputAdornment, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ScrollableWrapper from "../common/ScrollableWrapper";

type ChartKeyOption = {
  key: string;
  label: string;
  unit?: string;
};

type ChartKeyPickerProps = {
  selectedKeys: string[];
  onSelectionChange: (selected: string[]) => void;
  /** The sections, in order ("PNOE metrics", "Biomarkers", "Wearable data"). */
  sections: {
    title: string;
    data: ChartKeyOption[];
    /** Shown instead of "No options available" while a section's data is on its way. */
    emptyMessage?: string;
  }[];
  /**
   * Layout. Four columns and a tall card by default: the History Timeline's
   * three sections hold ~70 variables, and a 400px card made that a long
   * scroll (Giannis).
   */
  columns?: number;
  width?: number;
  maxHeight?: string | number;
  /** Selection cap: at the cap the unchecked options are disabled. Omitted
   *  means no cap. */
  max?: number;
};

/**
 * The History Timeline's Filters picker: a search, the selection as removable
 * chips, then the options in sections of checkboxes. Formerly also the
 * dashboard chart's picker; that chart is gone.
 */
export const CustomCheckBox = ({ selected }: { selected: boolean }) => {
  if (selected) {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="checkbox-btn-checked" clipPath="url(#clip0_8478_90077)">
          <path
            id="Icon"
            d="M17.5 0H2.5C1.83696 0 1.20107 0.263392 0.732233 0.732233C0.263392 1.20107 0 1.83696 0 2.5L0 17.5C0 18.163 0.263392 18.7989 0.732233 19.2678C1.20107 19.7366 1.83696 20 2.5 20H17.5C18.163 20 18.7989 19.7366 19.2678 19.2678C19.7366 18.7989 20 18.163 20 17.5V2.5C20 1.83696 19.7366 1.20107 19.2678 0.732233C18.7989 0.263392 18.163 0 17.5 0V0ZM17.1337 6.50875L8.38375 15.2587C8.14934 15.4931 7.83146 15.6247 7.5 15.6247C7.16854 15.6247 6.85066 15.4931 6.61625 15.2587L2.86625 11.5087C2.74686 11.3934 2.65163 11.2555 2.58612 11.103C2.52061 10.9505 2.48613 10.7865 2.48469 10.6205C2.48324 10.4545 2.51487 10.2899 2.57772 10.1363C2.64057 9.98269 2.73339 9.84312 2.85076 9.72576C2.96812 9.60839 3.10769 9.51557 3.26131 9.45272C3.41493 9.38987 3.57953 9.35824 3.7455 9.35969C3.91148 9.36113 4.0755 9.39561 4.22801 9.46112C4.38051 9.52663 4.51844 9.62186 4.63375 9.74125L7.5 12.6075L15.3663 4.74125C15.4816 4.62186 15.6195 4.52663 15.772 4.46112C15.9245 4.39561 16.0885 4.36113 16.2545 4.35969C16.4205 4.35824 16.5851 4.38987 16.7387 4.45272C16.8923 4.51557 17.0319 4.60839 17.1492 4.72576C17.2666 4.84312 17.3594 4.98269 17.4223 5.13631C17.4851 5.28993 17.5168 5.45453 17.5153 5.6205C17.5139 5.78648 17.4794 5.9505 17.4139 6.10301C17.3484 6.25551 17.2531 6.39344 17.1337 6.50875Z"
            fill="#1064DB"
          />
        </g>
        <defs>
          <clipPath id="clip0_8478_90077">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  } else {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <g clipPath="url(#clip0_8478_90086)">
          <path
            d="M17.5 20H2.5C1.83696 20 1.20107 19.7366 0.732233 19.2678C0.263392 18.7989 0 18.163 0 17.5V2.5C0 1.83696 0.263392 1.20107 0.732233 0.732233C1.20107 0.263392 1.83696 0 2.5 0L17.5 0C18.163 0 18.7989 0.263392 19.2678 0.732233C19.7366 1.20107 20 1.83696 20 2.5V17.5C20 18.163 19.7366 18.7989 19.2678 19.2678C18.7989 19.7366 18.163 20 17.5 20ZM2.5 2.5V17.5H17.5V2.5H2.5Z"
            fill="#1064DB"
          />
        </g>
        <defs>
          <clipPath id="clip0_8478_90086">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  }
};

const ChartKeySection = ({
  title,
  data,
  selectedKeys,
  onSelectionChange,
  max,
  emptyMessage = "No options available",
  columns = 3,
}: {
  title: string;
  data: ChartKeyOption[];
  selectedKeys: string[];
  onSelectionChange: (selected: string[]) => void;
  max?: number;
  emptyMessage?: string;
  columns?: number;
}) => {
  const theme: Theme = useTheme();
  const atCap = max !== undefined && selectedKeys.length >= max;

  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle(theme)}>{title}</div>
      {data.length > 0 ? (
        <div style={styles.sectionContent(columns)}>
          {data.map((option) => (
            <MenuItem
              key={option.key}
              style={styles.menuItem}
              disabled={atCap && !selectedKeys.includes(option.key)}
              onClick={() => {
                const newSelected = selectedKeys.includes(option.key)
                  ? selectedKeys.filter((key) => key !== option.key)
                  : [...selectedKeys, option.key];
                onSelectionChange(newSelected);
              }}
            >
              <CustomCheckBox
                selected={selectedKeys.indexOf(option.key) > -1}
              />
              <div style={styles.menuItemLabel(theme)}>{option.label}</div>
            </MenuItem>
          ))}
        </div>
      ) : (
        <div style={styles.message(theme)}>{emptyMessage}</div>
      )}
    </div>
  );
};

const ChartKeyPicker = ({
  selectedKeys,
  onSelectionChange,
  sections,
  max,
  columns = 4,
  width = 940,
  maxHeight = "70vh",
}: ChartKeyPickerProps) => {
  const theme: Theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");

  // The selection in selection order, labelled from whichever section holds
  // the key. A key whose section is still loading has no label yet and waits.
  const selectedOptions = useMemo(() => {
    const byKey = new Map<string, ChartKeyOption>();
    sections.forEach((section) =>
      section.data.forEach((option) => byKey.set(option.key, option))
    );
    return selectedKeys
      .map((key) => byKey.get(key))
      .filter((option): option is ChartKeyOption => option !== undefined);
  }, [selectedKeys, sections]);

  const handleRemove = (key: string) =>
    onSelectionChange(selectedKeys.filter((k) => k !== key));

  const handleClearAll = () => onSelectionChange([]);

  const hasChips = selectedOptions.length > 0;
  const clearAll = (
    <Button sx={styles.clearButton(theme)} onClick={handleClearAll}>
      Clear all
    </Button>
  );

  // Filter options based on the search term
  const filteredOptions = (options: ChartKeyOption[]) => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <div style={styles.container(theme, width, maxHeight)}>
      <div style={styles.searchContainer}>
        <TextField
          placeholder="Search"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: styles.searchField(theme),
          }}
        />
      </div>
      {hasChips && (
        <Box sx={styles.selectedRow}>
          <Box sx={styles.selectedChips}>
            {selectedOptions.map((option) => (
              <Chip
                key={option.key}
                label={option.label}
                size="small"
                onDelete={() => handleRemove(option.key)}
                deleteIcon={<CloseIcon />}
                sx={styles.selectedChip(theme)}
              />
            ))}
          </Box>
          {clearAll}
        </Box>
      )}
      <div style={styles.filtersHeader(theme)}>
        <div style={styles.filtersTitle(theme)}>Filters</div>
        {/* Beside the chips when there are chips, else on the header line. */}
        {!hasChips && clearAll}
      </div>
      <ScrollableWrapper>
        {sections.map((section) => (
          <ChartKeySection
            key={section.title}
            title={section.title}
            data={filteredOptions(section.data)}
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChange}
            max={max}
            emptyMessage={section.emptyMessage}
            columns={columns}
          />
        ))}
      </ScrollableWrapper>
    </div>
  );
};

const styles = {
  container: (theme: Theme, width: number, maxHeight: string | number) => ({
    width: `${width}px`,
    maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    padding: "16px",
    borderRadius: "8px",
  }),
  searchContainer: {
    marginBottom: "16px",
  },
  selectedRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
  },
  selectedChips: {
    flex: 1,
    display: "flex",
    flexWrap: "wrap" as "wrap",
    gap: "8px",
  },
  // The same chip as CustomMultiSelectAutoComplete's tags.
  selectedChip: (theme: Theme) => ({
    height: "24px",
    backgroundColor: theme.colorTokens.N[30],
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    borderRadius: "8px",
    "& .MuiChip-label": {
      paddingLeft: "8px",
      paddingRight: "4px",
    },
    "& .MuiChip-deleteIcon": {
      fontSize: "14px",
      marginRight: "6px",
      color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
      "&:hover": {
        color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
        opacity: 0.7,
      },
    },
  }),
  searchField: (theme: Theme) => ({
    height: "34px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    "& fieldset.MuiOutlinedInput-notchedOutline": {
      borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
    },
    "&:hover fieldset.MuiOutlinedInput-notchedOutline": {
      borderColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    },
  }),
  filtersHeader: (theme: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: "8px",
    marginBottom: "16px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  filtersTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  clearButton: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    textTransform: "none" as "none",
    padding: "0",
    minHeight: "24px",
    whiteSpace: "nowrap" as "nowrap",
    "&:hover": {
      backgroundColor: "transparent",
    },
  }),
  section: {
    marginBottom: "14px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    marginBottom: "16px",
  }),
  sectionContent: (columns: number) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: "12px",
  }),
  menuItem: {
    padding: "4px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  message: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    textAlign: "center" as "center",
  }),
  menuItemLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    flex: 1,
    whiteSpace: "normal",
    wordBreak: "break-word" as "break-word",
  }),
};

export default ChartKeyPicker;
